import { SyntheticEvent, useCallback, useEffect, useState } from "react"

export const GROUP_MEMBERS_LISTBOX_MAX_HEIGHT_PX = 300

const VIEWPORT_MARGIN_PX = 16

const getAnchorElement = (target: Element) =>
  target.closest(".MuiOutlinedInput-root") ??
  target.closest(".MuiAutocomplete-root") ??
  target

const getAvailableHeight = (anchor: Element) => {
  const rect = getAnchorElement(anchor).getBoundingClientRect()
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight
  const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_MARGIN_PX
  const spaceAbove = rect.top - VIEWPORT_MARGIN_PX
  return Math.min(
    GROUP_MEMBERS_LISTBOX_MAX_HEIGHT_PX,
    Math.max(spaceBelow, spaceAbove, 0)
  )
}

export const useGroupMembersListboxMaxHeight = () => {
  const [maxHeight, setMaxHeight] = useState(GROUP_MEMBERS_LISTBOX_MAX_HEIGHT_PX)
  const [anchor, setAnchor] = useState<Element | null>(null)

  const update = useCallback((el: Element) => {
    setMaxHeight(getAvailableHeight(el))
  }, [])

  const onOpen = useCallback(
    (event: SyntheticEvent) => {
      const el = event.currentTarget
      setAnchor(el)
      update(el)
    },
    [update]
  )

  const onClose = useCallback(() => {
    setAnchor(null)
    setMaxHeight(GROUP_MEMBERS_LISTBOX_MAX_HEIGHT_PX)
  }, [])

  useEffect(() => {
    if (!anchor) {
      return
    }

    const onResize = () => update(anchor)
    const resizeObserver = new ResizeObserver(onResize)

    resizeObserver.observe(anchor)
    const root = anchor.closest(".MuiAutocomplete-root")
    if (root && root !== anchor) {
      resizeObserver.observe(root)
    }

    window.addEventListener("resize", onResize)
    window.visualViewport?.addEventListener("resize", onResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", onResize)
      window.visualViewport?.removeEventListener("resize", onResize)
    }
  }, [anchor, update])

  return {
    listboxMaxHeight: `${maxHeight}px`,
    onOpen,
    onClose,
  }
}
